import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { unitTypeService, UnitType } from '../../services/configurationService';
import { customToast } from '../../utils/customToast';
import { X, Ruler } from 'lucide-react';

interface UnitsModalProps {
  unit: UnitType | null;
  onClose: () => void;
  onSave: () => void;
}

const UnitsModal: React.FC<UnitsModalProps> = ({ unit, onClose, onSave }) => {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    type: 'countable' as 'weight' | 'volume' | 'length' | 'countable',
    is_active: true,
    sort_order: 0
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (unit) {
      setFormData({
        name: unit.name,
        short_name: unit.short_name,
        type: unit.type as any,
        is_active: unit.is_active,
        sort_order: unit.sort_order
      });
    }
  }, [unit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      customToast.error('Please enter a unit name');
      return;
    }

    if (!formData.short_name.trim()) {
      customToast.error('Please enter a short name');
      return;
    }

    try {
      setSaving(true);
      if (unit) {
        await unitTypeService.update(unit.id, formData);
        customToast.success('Unit updated successfully');
      } else {
        await unitTypeService.create(formData);
        customToast.success('Unit created successfully');
      }
      onSave();
    } catch (error: any) {
      const errorMsg = error.response?.data?.errors?.name?.[0] || 
                      error.response?.data?.errors?.short_name?.[0] || 
                      error.response?.data?.message ||
                      'Failed to save unit';
      customToast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] animate-fadeIn`}
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-md max-h-[90vh] flex flex-col rounded-xl shadow-2xl animate-scaleIn ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Ruler className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  {unit ? 'Edit Unit Type' : 'Add New Unit Type'}
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {unit ? 'Update unit details' : 'Create measurement unit'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className={`flex-1 overflow-y-auto ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'}`}>
        <form onSubmit={handleSubmit} id="unit-form" className="p-4 sm:p-5 md:px-6 py-4">
          <div className="space-y-4">
            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Unit Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                }`}
                placeholder="e.g., Kilogram"
                required
              />
            </div>

            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Short Name *
              </label>
              <input
                type="text"
                value={formData.short_name}
                onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500'
                }`}
                placeholder="e.g., kg"
                required
              />
            </div>

            <div>
              <label className={`block text-xs sm:text-sm font-medium mb-1.5 sm:mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Unit Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border rounded-lg focus:outline-none focus:ring-0 transition-colors touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-slate-300 text-slate-900'
                }`}
              >
                <option value="countable">Countable (Piece/Count)</option>
                <option value="weight">Weight</option>
                <option value="volume">Volume</option>
                <option value="length">Length</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded touch-manipulation"
              />
              <label htmlFor="is_active" className={`ml-2 text-xs sm:text-sm select-none ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Active
              </label>
            </div>
          </div>
        </form>
        </div>

        {/* Footer */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`w-full sm:flex-1 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 touch-manipulation ${
                theme === 'dark'
                  ? 'border-slate-600 text-slate-300 hover:bg-slate-700 hover:border-slate-500'
                  : 'border-slate-300 text-slate-700 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="unit-form"
              disabled={saving}
              className="w-full sm:flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 touch-manipulation bg-blue-500 hover:bg-blue-600"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UnitsModal;
